import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import TopNav from "../../../commonUI/TopHeader/TopHeader";
import withNavigate from "../../withNavigate";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import {
  CalendarDateIcon,
  CloseLineIcon,
  ResetIcon,
  ChevronDownIcon,
  CheckIcon,
} from "../../../commonUI/Icons/SvgIcon";
import {
  NOTIFICATION_CATEGORY_FILTERS,
  NOTIFICATION_CHANNEL_FILTERS,
} from "../../../../config/notificationConstants";
import NotificationOverview from "./NotificationOverview";
import NotificationHistory from "./NotificationHistory";
import { periodButtons } from "../../../../config/constants";

const PERIOD_BUTTONS = [
  { id: "7D", label: "7D", days: 7 },
  { id: "30D", label: "30D", days: 30 },
  { id: "3M", label: "3M", days: 90 },
  { id: "6M", label: "6M", days: 180 },
  { id: "12M", label: "12M", days: 365 },
];

const getDateRangeLabel = (period, startDate, endDate) => {
  if (startDate && endDate) {
    return `${moment(startDate).format("DD MMM YY")} - ${moment(endDate).format("DD MMM YY")}`;
  }
  if (period) {
    const btn = PERIOD_BUTTONS.find((b) => b.id === period);
    if (btn) {
      const end = moment();
      const start = moment().subtract(btn.days - 1, "days");
      return `${start.format("DD MMM YY")} - ${end.format("DD MMM YY")}`;
    }
  }
  return null;
};

// ── Reusable custom dropdown ──────────────────────────────────────────────────
const NotifDropdown = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="notif-dropdown" ref={ref}>
      <div className="notif-dropdown__label">{label}</div>
      <button
        className={`notif-dropdown__btn${open ? " notif-dropdown__btn--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <span>{selected.label}</span>
        <ChevronDownIcon className="notif-dropdown__chevron" />
      </button>
      {open && (
        <div className="notif-dropdown__menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              className={`notif-dropdown__item${value === opt.value ? " notif-dropdown__item--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              type="button"
            >
              <span className="notif-dropdown__check">
                {value === opt.value && <CheckIcon width={12} height={12} />}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// ── Main page ─────────────────────────────────────────────────────────────────
const RptNotification = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPeriod, setSelectedPeriod] = useState("7D");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState("all");

  const dateRangeLabel = getDateRangeLabel(
    selectedPeriod,
    customStart,
    customEnd,
  );

  const handleReset = () => {
    setSelectedPeriod("7D");
    setCustomStart(null);
    setCustomEnd(null);
    setSelectedCategory("all");
    setSelectedChannel("all");
  };

  const handlePeriodClick = (periodId) => {
    setSelectedPeriod(periodId);
    setCustomStart(null);
    setCustomEnd(null);
    setShowDatePicker(false);
  };

  const handleViewHistory = (categoryId) => {
    setActiveTab("history");
    setSelectedCategory(categoryId);
  };

  return (
    <div className="l-base-layout__content">
      <TopNav title="Notifications" />

      <div className="notif-page">
        {/* ── Filter Bar ── */}
        <div className="notif-filter-bar">
          <div className="notif-filter-bar__group">
            <span className="notif-filter-bar__label">Date range</span>
            <div className="notif-filter-bar__date-controls">
              <button
                className="notif-filter-bar__date-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <CalendarDateIcon width="16" height="16" />
                {customStart && customEnd
                  ? `${moment(customStart).format("DD MMM YY")} - ${moment(customEnd).format("DD MMM YY")}`
                  : "Custom range"}
                {customStart && customEnd && (
                  <div
                    className="notif-filter-bar__date-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomStart(null);
                      setCustomEnd(null);
                    }}
                  >
                    <CloseLineIcon width="14" height="14" />
                  </div>
                )}
              </button>
              <div className="notif-filter-bar__period-btns">
                {periodButtons?.slice(1).map((button) => (
                  <button
                    key={button.id}
                    className={`notif-period-btn ${
                      selectedPeriod === button.id
                        ? "notif-period-btn--active"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedPeriod(button.id);
                      setShowDatePicker(false);
                    }}
                  >
                    {button.label}
                  </button>
                ))}
              </div>
            </div>

            {showDatePicker && (
              <div
                className="notif-filter-bar__backdrop"
                onClick={() => setShowDatePicker(false)}
              />
            )}
            {showDatePicker && (
              <div className="notif-filter-bar__datepicker-dropdown">
                <DatePicker
                  selectsRange
                  className="filter-box date-picker"
                  startDate={customStart}
                  endDate={customEnd}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setCustomStart(start);
                    setCustomEnd(end);
                    if (start && end) {
                      setShowDatePicker(false);
                    }
                  }}
                  placeholderText="Anytime"
                  monthsShown={2}
                  inline
                  showIcon={false}
                />
              </div>
            )}
          </div>

          <div className="notif-filter-bar__group">
            <span className="notif-filter-bar__label">Message category</span>
            <NotifDropdown
              label=""
              options={NOTIFICATION_CATEGORY_FILTERS}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />
          </div>

          <div className="notif-filter-bar__group">
            <span className="notif-filter-bar__label">Channel</span>
            <NotifDropdown
              label=""
              options={NOTIFICATION_CHANNEL_FILTERS}
              value={selectedChannel}
              onChange={setSelectedChannel}
            />
          </div>

          <button className="notif-filter-bar__reset-btn" onClick={handleReset}>
            <ResetIcon width="16" height="16" />
            Reset
          </button>
        </div>

        <div className="custom-tabs mt-0">
          {["Overview", "History"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab.toLowerCase())}
              className={`tab-item ${
                activeTab === tab.toLowerCase() ? "active" : ""
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        {activeTab === "overview" && (
          <NotificationOverview
            selectedCategory={selectedCategory}
            selectedChannel={selectedChannel}
            onViewHistory={handleViewHistory}
          />
        )}
        {activeTab === "history" && (
          <NotificationHistory
            selectedCategory={selectedCategory}
            selectedChannel={selectedChannel}
          />
        )}
      </div>
    </div>
  );
};

RptNotification.propTypes = {
  userDetails: PropTypes.object,
};

export default withNavigate(RptNotification);
